import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, MapPin, Clock, Lock, Star } from "lucide-react";
import { api, errMsg, money, fmtDateTime, isoToPtWall, ptWallToIso } from "@/lib/api";
import { inputCls, areaCls, btnPrimary, btnOutline, btnGhost, label } from "@/lib/ui";

const LABEL = {
  requested: "Pickup requested", awaiting_buyer: "Waiting for buyer to confirm", awaiting_seller: "Waiting for seller to confirm",
  confirmed: "Confirmed", completed: "Sale completed", no_sale: "Met — no sale", did_not_happen: "Didn't happen",
  unresolved: "Unresolved", disputed: "No-show under review", cancelled: "Cancelled",
};

const TermsForm = ({ appt, onDone, onCancel }) => {
  const t = appt.confirmed_terms || appt.terms || {};
  const [price, setPrice] = useState(t.price ?? appt.price);
  const [start, setStart] = useState(isoToPtWall(t.start_at));
  const [loc, setLoc] = useState(t.location || "");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!start) return toast.error("Pick a date and time.");
    setBusy(true);
    try {
      await api.post(`/appointments/${appt.id}/terms`, { price: Number(price), start_at: ptWallToIso(start), location: loc });
      toast.success("Details sent for confirmation");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="terms-form" className="space-y-3 bg-white rounded-xl p-3 border border-slate-200">
      <div className="grid grid-cols-2 gap-2">
        <div><span className={label}>Price</span><input data-testid="terms-price-input" type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} className={inputCls} /></div>
        <div><span className={label}>Date & time (Pacific)</span><input data-testid="terms-start-input" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className={inputCls} /></div>
      </div>
      <div>
        <span className={label}>Meeting place (only you two see this)</span>
        <input data-testid="terms-location-input" value={loc} onChange={(e) => setLoc(e.target.value)} placeholder="e.g. Ralphs parking lot, Ventura & Woodman" className={inputCls} />
      </div>
      <div className="flex gap-2">
        <button data-testid="terms-submit-btn" disabled={busy || loc.length < 3} onClick={submit} className={btnPrimary + " h-10"}>Send for confirmation</button>
        {onCancel && <button onClick={onCancel} className={btnGhost}>Close</button>}
      </div>
    </div>
  );
};

const Outcome = ({ appt, onDone }) => {
  const [happened, setHappened] = useState(null);
  const [sold, setSold] = useState(true);
  const [noShow, setNoShow] = useState(false);
  const [nad, setNad] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/appointments/${appt.id}/outcome`, { happened, sold: happened && sold, other_no_show: !happened && noShow, not_as_described: nad, note });
      toast.success(data.status === "disputed" ? "No-show report filed for review" : "Thanks — answer recorded");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const opt = (on) => `flex-1 h-10 rounded-xl text-sm font-semibold border ${on ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-300"}`;
  return (
    <div data-testid="outcome-form" className="space-y-3 bg-white rounded-xl p-3 border border-slate-200">
      <p className="text-sm font-semibold">Did the pickup happen?</p>
      <div className="flex gap-2">
        <button data-testid="outcome-happened-yes" className={opt(happened === true)} onClick={() => setHappened(true)}>Yes, we met</button>
        <button data-testid="outcome-happened-no" className={opt(happened === false)} onClick={() => setHappened(false)}>No</button>
      </div>
      {happened === true && (
        <>
          <div className="flex gap-2">
            <button data-testid="outcome-sold-yes" className={opt(sold)} onClick={() => setSold(true)}>Item sold</button>
            <button data-testid="outcome-sold-no" className={opt(!sold)} onClick={() => setSold(false)}>Didn't sell</button>
          </div>
          {!sold && appt.role === "buyer" && (
            <label className="flex items-center gap-2 text-sm"><input data-testid="outcome-nad-checkbox" type="checkbox" checked={nad} onChange={(e) => setNad(e.target.checked)} /> The item differed materially from its description</label>
          )}
        </>
      )}
      {happened === false && (
        <label className="flex items-center gap-2 text-sm"><input data-testid="outcome-noshow-checkbox" type="checkbox" checked={noShow} onChange={(e) => setNoShow(e.target.checked)} /> I was there — the other person didn't show</label>
      )}
      {happened === false && noShow && (
        <textarea data-testid="outcome-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What happened? (shared with an admin reviewer)" className={areaCls} />
      )}
      <button data-testid="outcome-submit-btn" disabled={busy || happened === null} onClick={submit} className={btnPrimary + " h-10 w-full"}>Submit answer</button>
    </div>
  );
};

const Review = ({ appt }) => {
  const [mine, setMine] = useState(undefined);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  useEffect(() => {
    api.get(`/appointments/${appt.id}/my-review`).then(({ data }) => setMine(data.review)).catch(() => setMine(null));
  }, [appt.id]);
  const submit = async () => {
    try {
      await api.post(`/appointments/${appt.id}/review`, { rating, comment });
      toast.success("Rating submitted");
      setMine({ rating, comment });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (mine === undefined) return null;
  if (mine) return <p data-testid="review-submitted" className="text-sm text-slate-600">You rated this {mine.rating}/5. Ratings publish once both of you submit, or after the review window.</p>;
  return (
    <div data-testid="review-form" className="space-y-2 bg-white rounded-xl p-3 border border-slate-200">
      <p className="text-sm font-semibold">Rate this transaction</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`review-star-${n}`} onClick={() => setRating(n)} aria-label={`${n} stars`}>
            <Star className={`h-7 w-7 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
          </button>
        ))}
      </div>
      <textarea data-testid="review-comment-input" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Optional comment" className={areaCls} />
      <button data-testid="review-submit-btn" disabled={!rating} onClick={submit} className={btnPrimary + " h-10"}>Submit rating</button>
    </div>
  );
};

const ConfirmedActions = ({ appt, onChange }) => {
  const [mode, setMode] = useState(null);
  const [reason, setReason] = useState("");
  const [nad, setNad] = useState(false);
  const started = new Date(appt.confirmed_terms.start_at).getTime() - 30 * 60000 <= Date.now();
  const late = async (minutes) => {
    try {
      await api.post(`/appointments/${appt.id}/late`, { minutes });
      toast.success("Running-late message sent");
      setMode(null);
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const cancel = async () => {
    try {
      const { data } = await api.post(`/appointments/${appt.id}/cancel`, { reason, not_as_described: nad });
      toast.success(data.cancel_type === "late" ? "Pickup cancelled (late cancellation)" : "Pickup cancelled");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (started && appt.status === "confirmed") {
    if (appt.outcome?.[appt.role]) return <p data-testid="outcome-waiting" className="text-sm text-slate-600">Thanks — waiting for the other person's answer.</p>;
    return (
      <div className="space-y-2">
        <Outcome appt={appt} onDone={onChange} />
        <button data-testid="appt-late-btn" className={btnGhost} onClick={() => late(10)}>I'm running late (10 min)</button>
      </div>
    );
  }
  const proposal = appt.proposal ? <Proposal appt={appt} onChange={onChange} /> : null;
  return (
    <div className="space-y-2">
      {proposal}
      {!mode && (
        <div className="flex flex-wrap gap-2">
          <button data-testid="appt-late-btn" className={btnOutline + " h-10"} onClick={() => setMode("late")}><Clock className="h-4 w-4" /> Running late</button>
          {!appt.proposal && <button data-testid="appt-reschedule-btn" className={btnOutline + " h-10"} onClick={() => setMode("reschedule")}>Reschedule</button>}
          <button data-testid="appt-cancel-btn" className={btnGhost + " text-red-600 hover:bg-red-50"} onClick={() => setMode("cancel")}>Cancel</button>
        </div>
      )}
      {mode === "late" && (
        <div className="flex flex-wrap gap-2">
          {[10, 15, 30].map((m) => <button key={m} data-testid={`appt-late-${m}`} className={btnOutline + " h-10"} onClick={() => late(m)}>{m} min</button>)}
          <button className={btnGhost} onClick={() => setMode(null)}>Close</button>
        </div>
      )}
      {mode === "reschedule" && <TermsForm appt={appt} onDone={() => { setMode(null); onChange(); }} onCancel={() => setMode(null)} />}
      {mode === "cancel" && <CancelForm {...{ appt, reason, setReason, nad, setNad, cancel, close: () => setMode(null) }} />}
    </div>
  );
};

const Proposal = ({ appt, onChange }) => {
  const p = appt.proposal;
  const mine = p.by === appt.role;
  const call = async (path, body, msg) => {
    try {
      await api.post(`/appointments/${appt.id}/${path}`, body);
      toast.success(msg);
    } catch (e) {
      toast.error(errMsg(e));
    }
    onChange();
  };
  return (
    <div data-testid="reschedule-proposal" className="space-y-2 bg-white rounded-xl p-3 border border-amber-300">
      <p className="text-xs font-bold uppercase text-amber-700">Proposed change {mine ? "(sent by you)" : ""}</p>
      <p className="text-sm">{money(p.terms.price)} · {fmtDateTime(p.terms.start_at)} · {p.terms.location}</p>
      <p className="text-xs text-slate-500">The pickup above stays confirmed until both of you agree.</p>
      <div className="flex flex-wrap gap-2">
        {!mine && <button data-testid="proposal-agree-btn" onClick={() => call("agree", { version: appt.version }, "New details confirmed")} className={btnPrimary + " h-10"}>Agree to change</button>}
        <button data-testid="proposal-clear-btn" onClick={() => call("proposal/clear", {}, mine ? "Change withdrawn" : "Kept original details")} className={btnOutline + " h-10"}>{mine ? "Withdraw change" : "Keep original"}</button>
      </div>
    </div>
  );
};

const CancelForm = ({ appt, reason, setReason, nad, setNad, cancel, close }) => {
  const hoursLeft = appt.confirmed_terms ? (new Date(appt.confirmed_terms.start_at) - Date.now()) / 3.6e6 : null;
  return (
    <div data-testid="cancel-form" className="space-y-2 bg-white rounded-xl p-3 border border-slate-200">
      {hoursLeft != null && hoursLeft < 24 && <p className="text-xs text-amber-700">Cancelling within 24 hours counts as a late cancellation on your record.</p>}
      <input data-testid="cancel-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (shared with the other person)" className={inputCls} />
      {appt.role === "buyer" && appt.ever_confirmed && (
        <label className="flex items-center gap-2 text-sm"><input data-testid="cancel-nad-checkbox" type="checkbox" checked={nad} onChange={(e) => setNad(e.target.checked)} /> Item differs materially from the listing</label>
      )}
      <div className="flex gap-2">
        <button data-testid="cancel-confirm-btn" onClick={cancel} className={btnPrimary + " h-10 bg-red-600 hover:bg-red-700"}>Cancel pickup</button>
        <button onClick={close} className={btnGhost}>Keep it</button>
      </div>
    </div>
  );
};

export const AppointmentCard = ({ appt, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [reason, setReason] = useState("");
  const [nad, setNad] = useState(false);
  const t = appt.status === "confirmed" ? appt.confirmed_terms : appt.terms;
  const ct = appt.confirmed_terms;
  const other = appt.role === "buyer" ? "seller" : "buyer";
  const agree = async () => {
    try {
      await api.post(`/appointments/${appt.id}/agree`, { version: appt.version });
      toast.success("Pickup confirmed by both of you");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
      onChange();
    }
  };
  const cancel = async () => {
    try {
      await api.post(`/appointments/${appt.id}/cancel`, { reason, not_as_described: nad });
      toast.success("Pickup cancelled");
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const pendingChange = appt.status.startsWith("awaiting_");
  return (
    <div data-testid={`appointment-card-${appt.id}`} className="rounded-2xl p-4 space-y-3 max-w-md w-full bg-gradient-to-br from-blue-50 to-white border border-blue-200">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-blue-700">
        <CalendarCheck className="h-4 w-4" /> <span data-testid="appointment-status">{LABEL[appt.status]}</span>
        {appt.reservation_held && <span className="ml-auto inline-flex items-center gap-1 text-[10px] bg-blue-600 text-white px-2 py-0.5 rounded-full"><Lock className="h-3 w-3" /> Reserved</span>}
      </div>
      {!t && <p className="text-sm text-slate-700">Buyer proposed: <b>{appt.proposed_window}</b> · {money(appt.price)}{appt.note ? ` — “${appt.note}”` : ""}</p>}
      {t && (
        <div className="space-y-1.5 text-sm text-slate-800">
          <div className="flex items-center gap-2"><span data-testid="appointment-price" className="text-2xl font-extrabold font-mono">{money(t.price)}</span></div>
          <div data-testid="appointment-time" className="flex items-center gap-2"><Clock className="h-4 w-4 text-slate-500" /> {fmtDateTime(t.start_at)}</div>
          <div data-testid="appointment-location" className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-500" /> {t.location}</div>
        </div>
      )}
      {pendingChange && ct && (
        <p className="text-xs text-slate-600 bg-white rounded-lg p-2 border">Currently confirmed: {money(ct.price)} · {fmtDateTime(ct.start_at)} · {ct.location}. Changes need both of you to agree.</p>
      )}
      {appt.status === "confirmed" && appt.reservation_expires_at && <p className="text-xs text-slate-500">Reservation expires {fmtDateTime(appt.reservation_expires_at)}.</p>}
      {appt.email_log?.length > 0 && (
        <p data-testid="appointment-email-status" className={`text-xs ${appt.email_log[0].status === "failed" ? "text-red-600" : "text-slate-500"}`}>
          Email {appt.email_log[0].kind.replace("_", " ")}: {{ sent: "sent", accepted: "accepted for sending", failed: "failed to send — check the app for details", skipped: "not sent (turned off or test address)", not_configured: "not configured — in-app only" }[appt.email_log[0].status]}
        </p>
      )}

      {appt.status === "requested" && appt.role === "seller" && <TermsForm appt={appt} onDone={onChange} />}
      {appt.status === "requested" && appt.role === "buyer" && <p className="text-sm text-slate-600">Waiting for the seller to confirm price, time and meeting place.</p>}
      {appt.status === `awaiting_${appt.role}` && !editing && (
        <div className="flex flex-wrap gap-2">
          <button data-testid="appt-agree-btn" onClick={agree} className={btnPrimary + " h-10"}>Confirm these details</button>
          <button data-testid="appt-suggest-btn" onClick={() => setEditing(true)} className={btnOutline + " h-10"}>Suggest changes</button>
        </div>
      )}
      {appt.status === `awaiting_${other}` && <p className="text-sm text-slate-600">Sent. Waiting for the {other} to confirm these exact details.</p>}
      {editing && <TermsForm appt={appt} onDone={() => { setEditing(false); onChange(); }} onCancel={() => setEditing(false)} />}
      {appt.status === "confirmed" && <ConfirmedActions appt={appt} onChange={onChange} />}
      {["requested", "awaiting_buyer", "awaiting_seller"].includes(appt.status) && !cancelMode && (
        <button data-testid="appt-cancel-request-btn" onClick={() => setCancelMode(true)} className={btnGhost + " text-red-600 hover:bg-red-50"}>Cancel request</button>
      )}
      {cancelMode && <CancelForm {...{ appt, reason, setReason, nad, setNad, cancel, close: () => setCancelMode(false) }} />}
      {appt.status === "completed" && <Review appt={appt} />}
      {appt.status === "unresolved" && <p className="text-sm text-slate-600">Answers didn't match or someone didn't respond, so no sale was recorded.</p>}
      {appt.status === "disputed" && <p className="text-sm text-slate-600">A no-show report is being reviewed. Both sides can respond on the Pickups page.</p>}
    </div>
  );
};
